import { Link } from 'react-router-dom';

import { Card } from '../components/ui/Card';

const sections = [
  {
    title: '1. First-time setup',
    body: (
      <>
        <p>
          Open <strong>Settings</strong> and create a broker account. Choose its mandate separately
          from its broker. The broker controls connectivity and imports; the mandate controls the
          strategy workflow.
        </p>
        <p>
          <strong>Swing Trading</strong> is for short/medium-duration positions managed using
          entries, stops, R-risk and execution discipline. <strong>Long-Term Investing</strong> is
          for multi-year investments managed using thesis, valuation, allocation and portfolio
          construction.
        </p>
        <p>
          Long-Term Investing accounts retain imports, positions, executions, and historical
          TradePlans, but they do not use active swing TradePlan actions. Open{' '}
          <strong>Investing</strong> to record a separate thesis for an imported holding. A holding
          remains importable when no thesis exists and is labelled <strong>Thesis missing</strong>.
          You can also create a manual thesis by entering an independent security identity or
          reference and a display symbol; the app does not infer identity from the symbol.
        </p>
        <p>
          Investment theses record the role, long-term case, invalidating conditions, optional
          allocation intentions, KPIs, valuation notes, and review timing. They do not add stops, R
          targets, automated valuation, allocation advice, or broker actions. If an imported HL
          identity changes, compare the recorded and latest identities, affirm that they are the
          same investment, and enter an audit reason before the thesis is linked again. If the
          account mandate later changes, preserved theses remain readable but cannot be changed or
          superseded unless the account is classified as Long-Term Investing again.
        </p>
        <p>
          The <strong>Allocation</strong> view on Investing uses the latest imported HL holdings
          snapshot for the selected Long-Term Investing account only. Account value is the
          snapshot&apos;s total value (holdings plus cash); each holding&apos;s weight is its value
          divided by that account value. Cash available for allocation is HL&apos;s amount available
          to invest. Only an <strong>ACTIVE</strong> thesis supplies a target and maximum. Within
          &plusmn;1 percentage point of target (inclusive) is <strong>Near target</strong>; further
          below or above is <strong>Below target</strong> or <strong>Above target</strong>; strictly
          more than the maximum is <strong>Above maximum</strong>, which takes precedence. An unset
          target or maximum is shown as unset with no comparison, and ambiguous or unconfirmed
          identities stay unmatched. If the snapshot is missing, does not reconcile, or is in a
          different currency from the account, values show <strong>Data unavailable</strong> rather
          than being converted or estimated. HL reports its valuation time in UK time, shown as GMT
          or BST, and valuations more than 7 days older than that real time are flagged as stale. If
          the reported time falls in the hour a UK clock change repeats or skips, it is shown as
          reported and staleness is stated only when it is clear either way. These are facts, not
          buy or sell recommendations.
        </p>
        <p>
          After creating the account, use <strong>Update NAV</strong>. NAV is used by sizing and
          risk screens, so stale or missing NAV makes risk information less useful.
        </p>
        <p>
          In Notification Preferences you can configure weekly-review, import, journal, review, and
          rule-change alerts. Breach notifications are always enabled.
        </p>
      </>
    ),
  },
  {
    title: '2. Connect IBKR',
    body: (
      <>
        <p>
          For an IBKR account, Settings shows an <strong>Auto-Sync</strong> card. Enter your IBKR
          Flex token, Trade Query ID, and optionally a NAV Query ID.
        </p>
        <p>
          Use <strong>Test connection</strong> before relying on synchronization. Use{' '}
          <strong>Sync now</strong> for an immediate refresh.
        </p>
        <p>
          When the Flex worker and scheduler are running, enabled IBKR accounts are scheduled every
          two hours on the UTC clock. Flex is delayed synchronization, not a live execution feed;
          IBKR remains the source of truth for immediate execution status.
        </p>
      </>
    ),
  },
  {
    title: '3. Import trades manually',
    body: (
      <>
        <p>
          Open <strong>Import</strong>, select the account, upload the broker CSV, review parsed
          rows, duplicates and errors, then commit the clean staged import.
        </p>
        <p>
          Commit is disabled while error rows remain. After a successful commit, open Positions to
          verify the recalculated position.
        </p>
        <p>
          Import history is available in Settings. Deleting an import removes its imported trades.{' '}
          <strong>Purge all trades</strong> is destructive and requires typing PURGE.
        </p>
      </>
    ),
  },
  {
    title: '4. Plan before trading',
    body: (
      <>
        <p>
          Open <strong>Plan</strong> before entering a new trade. Enter the account, symbol,
          direction, reference entry, stop, target, trade type, conviction, thesis, and the other
          requested fields. You may also set a planned entry minimum and maximum; provide both, and
          the range is frozen with the plan for later execution comparison.
        </p>
        <p>
          The rules gate previews the decision and recommended position size. A blocked plan cannot
          be submitted. A warning requires an override reason. A passing plan can be submitted
          normally.
        </p>
        <p>
          For a standard swing plan the frozen profit contract is TP1: 20% at 1.5R; TP2: 25% at
          2.5R; TP3: 25% at 3.5R; the runner holds the remaining whole shares. After TP2, move the
          stop to at least breakeven. Stops may tighten but should not be widened.
        </p>
        <p>Submitting the plan creates a pre-trade journal entry automatically.</p>
      </>
    ),
  },
  {
    title: '5. Synchronization and plan attribution',
    body: (
      <>
        <p>
          Committed broker executions update positions and discipline information. A newly imported
          opening fill is automatically attached to a frozen swing plan only when exactly one
          compatible plan exists for the same user, account, symbol, direction, and valid plan
          window.
        </p>
        <p>
          The app does not overwrite an existing plan link or guess when multiple plans could match.
          Exit fills are reconciled explicitly from the position lifecycle when required.
        </p>
      </>
    ),
  },
  {
    title: '6. Monitor positions',
    body: (
      <>
        <p>
          Open <strong>Positions</strong> to see current positions, stops, days held, status flags,
          and Trading Next Actions for a Swing Trading account. The action list computes one current
          priority per open position and also surfaces required closing-trade reviews. Correcting
          the underlying position, stop, execution, or review state removes the action
          automatically. Click a symbol for its lifecycle.
        </p>
        <p>
          <strong>Portfolio Heat</strong> is current entry-to-stop capital risk for the Swing
          Trading account. It uses average entry, current stop, and absolute open quantity; live
          quotes do not change it. The percentage values are shares of account NAV shown as
          percentage points.
        </p>
        <p>
          Keep stops current. If any open position cannot be calculated because its stop, execution
          history, or currency is unresolved, Portfolio Heat is marked <strong>Incomplete</strong>{' '}
          and remaining budget is unavailable rather than understated. Stale NAV remains usable but
          is called out explicitly. Use Recompute positions when derived positions need rebuilding
          from stored trades; note that recomputation can remove manual overrides after
          confirmation.
        </p>
        <p>
          A missing-stop discipline event means no active stop was recorded in DisciplinedTrader at
          that time. It does not assert whether a protective order existed at the broker because
          broker open-order state is unavailable.
        </p>
      </>
    ),
  },
  {
    title: '7. Position lifecycle and exit reconciliation',
    body: (
      <>
        <p>
          The lifecycle combines execution discipline, trade replay, quantity and cost, realised and
          unrealised P&amp;L, stop history, and the chronological trade/plan timeline.
        </p>
        <p>
          The <strong>Execution discipline</strong> card compares actual exits with the frozen
          contract. If an unlinked exit genuinely belongs to the plan, use{' '}
          <strong>Link to frozen plan</strong>.
        </p>
        <p>
          Linking does not rewrite history. The actual fill price is compared with the original
          target, so an exit can remain <strong>OFF PLAN</strong> even after it is correctly linked.
        </p>
      </>
    ),
  },
  {
    title: '8. Dashboard and Rules',
    body: (
      <>
        <p>
          Use <strong>Dashboard</strong> as the daily control screen for portfolio discipline,
          unrealised P&amp;L, Portfolio Heat, NAV history, behavioural flags, drawdown, P&amp;L
          history, open-position concentration, and pending reviews.
        </p>
        <p>
          Portfolio Heat: Incomplete means at least one open position has unresolved risk data. A
          Stale NAV warning means NAV should be updated in Settings or the NAV Flex sync should be
          checked.
        </p>
        <p>
          Use <strong>Rules</strong> to review gate thresholds, severity and enabled state. Treat
          rule changes as changes to your trading process, not a way to make a specific trade pass.
        </p>
      </>
    ),
  },
  {
    title: '9. Reviews and Journal',
    body: (
      <>
        <p>
          <strong>Reviews</strong> contains pending, recent and historical trade reviews. Reviews
          record plan adherence and dominant emotion; deep reviews also ask what happened and what
          you would change.
        </p>
        <p>
          <strong>Weekly Review</strong> summarizes P&amp;L, NAV change, drawdown, win rate,
          expectancy, profit factor, R distribution, pre-trade adherence, rule issues,
          winners/losers, behavioural flags and lessons.
        </p>
        <p>
          <strong>Journal</strong> is the chronological decision record. Plans create PRE TRADE
          entries automatically, and entries can be filtered by date, type and ticker.
        </p>
      </>
    ),
  },
  {
    title: '10. Analytics and Market Console',
    body: (
      <>
        <p>
          <strong>Analytics</strong> reviews realised performance from closed FIFO lots across 7D,
          30D, 90D, YTD and all-time periods, including win rate, profit factor, average R,
          cumulative P&amp;L, calendar, symbol performance and behavioural-flag impact.
        </p>
        <p>
          <strong>Market Console</strong> groups market context in one place. <strong>Now</strong>{' '}
          turns it into a plan for the next session: a stance from the QQQ outlook and breadth, key
          signals, leading themes and a chart queue; the Dashboard shows the same stance.{' '}
          <strong>Health</strong> shows the QQQ regime using trend, distribution, outlook, internals
          and a watchlist. <strong>Pulse</strong> shows Stockbee breadth: ±4% movers, 5- and 10-day
          ratios, momentum counts and T2108. <strong>Flow</strong> ranks theme and sector ETFs by
          horizon, with top holdings, extended-hours moves and industry leadership; edit the ETF
          lists in Settings. <strong>Crowding</strong> shows weekly CFTC futures positioning and
          flags speculator extremes. <strong>Stocks</strong> lists non-extended momentum leaders,
          the ones in a tight coil, and super liquid leaders; the star adds a symbol to your
          watchlist. Pick a <strong>Replay date</strong> to see Now, Pulse, Flow, Crowding and
          Stocks as they were stored on that date. All of it is informational context for
          risk-taking, not a standalone buy or sell signal.
        </p>
      </>
    ),
  },
] as const;

export function Help() {
  return (
    <main className="min-h-screen bg-bg text-text">
      <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6">
        <header>
          <h1 className="text-2xl font-semibold">Help</h1>
          <p className="mt-1 text-sm text-text-muted">
            How to use DisciplinedTrader from planning through review.
          </p>
        </header>

        <Card className="border-primary/40">
          <h2 className="text-lg font-semibold">The core workflow</h2>
          <p className="mt-2 text-sm text-text-muted">
            Plan first → size from risk → execute at the broker → synchronize → reconcile → review →
            learn.
          </p>
          <p className="mt-3 text-sm">
            DisciplinedTrader is a discipline and review system. It does not place broker orders.
          </p>
        </Card>

        <Card>
          <h2 className="text-lg font-semibold">Recommended daily workflow</h2>
          <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-text-muted">
            <li>Check Dashboard for stale NAV, missing stops, drawdown and behavioural alerts.</li>
            <li>Check the Market Console before deciding how much risk to take.</li>
            <li>Create the Plan before entering any new swing trade at the broker.</li>
            <li>Read the gate decision and use the recommended position size.</li>
            <li>Execute at the broker, then synchronize with IBKR Flex or Import.</li>
            <li>Verify quantity, stop, risk and next required action in Positions.</li>
            <li>
              After reductions or exits, open the lifecycle and reconcile genuine unlinked fills.
            </li>
            <li>
              Complete pending trade reviews and use the weekly review to identify one behaviour to
              keep and one to change.
            </li>
          </ol>
        </Card>

        {sections.map((section) => (
          <Card key={section.title}>
            <h2 className="text-lg font-semibold">{section.title}</h2>
            <div className="mt-3 space-y-3 text-sm leading-6 text-text-muted">{section.body}</div>
          </Card>
        ))}

        <Card>
          <h2 className="text-lg font-semibold">Common problems</h2>
          <div className="mt-3 space-y-4 text-sm text-text-muted">
            <div>
              <strong className="text-text">IBKR trades are missing:</strong> check Settings →
              Auto-Sync, Last sync status, Enabled, token/query configuration, Test connection, then
              Sync now. Use manual Import if necessary.
            </div>
            <div>
              <strong className="text-text">Position size is wrong:</strong> verify executions were
              imported into the correct account, then consider Recompute positions.
            </div>
            <div>
              <strong className="text-text">Portfolio risk looks too low:</strong> check Positions
              for missing or invalid stops.
            </div>
            <div>
              <strong className="text-text">Plan will not submit:</strong> read the gate result.
              Blocked plans cannot submit; warnings require an explicit override reason.
            </div>
            <div>
              <strong className="text-text">Exit is unlinked:</strong> open the position lifecycle
              and link it only if the fill genuinely belongs to that frozen plan.
            </div>
            <div>
              <strong className="text-text">Linked exit is OFF PLAN:</strong> this can be correct.
              Linking identifies the plan; it does not make the execution price compliant.
            </div>
          </div>
        </Card>

        <div className="flex flex-wrap gap-2 pb-4">
          <Link
            className="rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover"
            to="/dashboard"
          >
            Back to Dashboard
          </Link>
          <Link
            className="rounded border border-border px-4 py-2 text-sm text-text-muted hover:bg-surface"
            to="/settings"
          >
            Settings
          </Link>
        </div>
      </div>
    </main>
  );
}
